import React, { useContext, useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { Context } from "../../../utils/context";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import ModalSave from "../../common/ModelSave";

const EditSubscription = ({ show, handleClose, brokerId, subscription }) => {
  const { postData, getData } = useContext(Context);

  const [showModal, setShowModal] = useState({
    code: 0,
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      payment_status: "Pending",
      payment_id: "",
      payment_date: "",
      s_date: "",
      e_date: "",
      status: false,
    },
  });

  const sDate = watch("s_date");

  const paymentStatus = watch("payment_status");

  useEffect(() => {
    if (!show || !brokerId) return;

    const getSubscriptionDetails = async () => {
      try {
        setLoading(true);

        const response = await getData(
          `/admin/employee-management/users/subscription/${brokerId}`,
        );

        console.log("SUBSCRIPTION API RESPONSE:", response);

        if (response?.success) {
          const data = response?.data;

          reset({
            payment_status: data?.payment_status || "Pending",
            payment_id: data?.payment_id || "",

            payment_date: data?.payment_date
              ? new Date(data.payment_date).toISOString().split("T")[0]
              : "",

            s_date: data?.s_date
              ? new Date(data.s_date).toISOString().split("T")[0]
              : "",

            e_date: data?.e_date
              ? new Date(data.e_date).toISOString().split("T")[0]
              : "",

            status: data?.status || false,
          });
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
      } finally {
        setLoading(false);
      }
    };

    getSubscriptionDetails();
  }, [show, brokerId, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const sendData = {
        payment_status: data.payment_status,
        payment_id: data.payment_id?.trim() || null,
        payment_date: data.payment_date || null,
        s_date: data.s_date,
        e_date: data.e_date,
        status: data.status,
      };

      const response = await postData(
        `/admin/employee-management/users/subscription/${subscription?.id}`,
        sendData,
      );

      if (response?.success) {
        setShowModal({
          code: response.code,
          message: "Subscription details updated successfully",
        });

        setTimeout(() => {
          setShowModal({
            code: 0,
            message: "",
          });

          handleClose();
        }, 1000);
      } else {
        setShowModal({
          code: response?.code,
          message:
            response?.errors || response?.message || "Something went wrong",
        });

        setTimeout(() => {
          setShowModal({
            code: 0,
            message: "",
          });
        }, 1000);
      }
    } catch (error) {
      console.error("Error updating subscription:", error);

      setShowModal({
        code: 500,
        message:
          error?.response?.data?.message ||
          "Something went wrong while updating subscription",
      });

      setTimeout(() => {
        setShowModal({
          code: 0,
          message: "",
        });
      }, 1000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Subscription / Payment Details</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            {loading ? (
              <div className="text-center py-3">
                Loading subscription details...
              </div>
            ) : (
              <>
                {/* Subscription Plan - Non Editable */}
                <div className="mb-3">
                  <label className="form-label">Subscription Plan</label>

                  <input
                    type="text"
                    className="form-control"
                    value={
                      subscription?.Broker_Subscription_Plan?.name ||
                      subscription?.plan_name ||
                      ""
                    }
                    disabled
                    readOnly
                  />
                </div>

                {/* Amount - Non Editable */}
                <div className="mb-3">
                  <label className="form-label">Amount</label>

                  <input
                    type="text"
                    className="form-control"
                    value={subscription?.amount || ""}
                    disabled
                    readOnly
                  />
                </div>

                {/* Payment Status */}
                <div className="mb-3">
                  <Form.Label>Payment Status</Form.Label>

                  <Form.Select
                    className={classNames({
                      "is-invalid": errors.payment_status,
                    })}
                    {...register("payment_status", {
                      required: "Payment Status is required",
                    })}
                  >
                    <option value="">Select Payment Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                  </Form.Select>

                  {errors.payment_status && (
                    <span className="text-danger">
                      {errors.payment_status.message}
                    </span>
                  )}

                  {errors.payment_status && (
                    <span className="text-danger">
                      {errors.payment_status.message}
                    </span>
                  )}
                </div>

                {/* Payment / Transaction ID */}
                <div className="mb-3">
                  <Form.Label>Transaction ID</Form.Label>

                  <Form.Control
                    type="text"
                    placeholder="Enter transaction ID"
                    className={classNames({
                      "is-invalid": errors.payment_id,
                    })}
                    {...register("payment_id", {
                      required:
                        paymentStatus === "Paid"
                          ? "Transaction ID is required"
                          : false,

                      validate: (value) =>
                        !value?.trim() ||
                        value.trim().length <= 200 ||
                        "Transaction ID must be 200 characters or less",
                    })}
                  />

                  {errors.payment_id && (
                    <span className="text-danger">
                      {errors.payment_id.message}
                    </span>
                  )}
                </div>

                {/* Payment Date */}
                <div className="mb-3">
                  <Form.Label>Payment Date</Form.Label>

                  <Form.Control
                    type="date"
                    className={classNames({
                      "is-invalid": errors.payment_date,
                    })}
                    {...register("payment_date", {
                      required:
                        paymentStatus === "Paid"
                          ? "Payment Date is required"
                          : false,
                    })}
                  />

                  {errors.payment_date && (
                    <span className="text-danger">
                      {errors.payment_date.message}
                    </span>
                  )}
                </div>

                {/* Subscription Dates */}
                <div className="row">
                  {/* Start Date */}
                  <div className="col-md-6 mb-3">
                    <Form.Label>Subscription Start Date</Form.Label>

                    <Form.Control
                      type="date"
                      className={classNames({
                        "is-invalid": errors.s_date,
                      })}
                      {...register("s_date", {
                        required: "Subscription start date is required",
                        validate: (value) => {
                          if (!value) {
                            return "Subscription start date is required";
                          }

                          return (
                            !isNaN(new Date(value).getTime()) ||
                            "Please enter a valid start date"
                          );
                        },
                      })}
                    />

                    {errors.s_date && (
                      <span className="text-danger">
                        {errors.s_date.message}
                      </span>
                    )}
                  </div>

                  {/* End Date */}
                  <div className="col-md-6 mb-3">
                    <Form.Label>Subscription End Date</Form.Label>

                    <Form.Control
                      type="date"
                      min={sDate || undefined}
                      className={classNames({
                        "is-invalid": errors.e_date,
                      })}
                      {...register("e_date", {
                        required: "Subscription end date is required",

                        validate: (value) => {
                          if (!value) {
                            return "Subscription end date is required";
                          }

                          if (!sDate) {
                            return true;
                          }

                          return (
                            new Date(value) >= new Date(sDate) ||
                            "End date must be greater than or equal to start date"
                          );
                        },
                      })}
                    />

                    {errors.e_date && (
                      <span className="text-danger">
                        {errors.e_date.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div className="mb-3">
                  <Form.Check
                    type="switch"
                    label={
                      watch("status")
                        ? "Subscription Active"
                        : "Subscription Inactive"
                    }
                    {...register("status")}
                  />
                </div>
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Close
            </Button>

            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Details"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ModalSave
        message={showModal.message}
        showErrorModal={showModal.code ? true : false}
      />
    </>
  );
};

export default EditSubscription;
